/**
 * liveToggle — shared, persistent Live Prices toggle state. A per-device
 * boolean gating client-side live refreshes (EODHD, via /api/symbols/live),
 * stored in localStorage (`vip_livePrices`) and intentionally NOT synced to KV
 * — this is a per-device preference, not a portfolio property. Default OFF:
 * the nightly mirror writes EOD prices to KV regardless; this only controls
 * whether the browser additionally fires live calls to overlay intraday data.
 */

const STORAGE_KEY = 'vip_livePrices';

type Listener = (enabled: boolean) => void;
type FetchedListener = (ts: number) => void;

const enabledListeners = new Set<Listener>();
const fetchedListeners = new Set<FetchedListener>();

let lastLiveFetchAt: number | null = null;

function readFromStorage(): boolean {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return false;
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw == null) return false;
    return raw === 'true' || raw === '1';
  } catch {
    return false;
  }
}

export function isLiveEnabled(): boolean {
  return readFromStorage();
}

export function setLiveEnabled(enabled: boolean): void {
  const next = !!enabled;
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(STORAGE_KEY, next ? 'true' : 'false');
    }
  } catch {
    // quota / disabled — non-fatal.
  }
  for (const fn of enabledListeners) {
    try { fn(next); } catch { /* swallow */ }
  }
}

export function subscribe(fn: Listener): () => void {
  enabledListeners.add(fn);
  return () => { enabledListeners.delete(fn); };
}

export function markLiveFetched(): void {
  lastLiveFetchAt = Date.now();
  for (const fn of fetchedListeners) {
    try { fn(lastLiveFetchAt); } catch { /* swallow */ }
  }
}

export function getLastLiveFetchAt(): number | null {
  return lastLiveFetchAt;
}

export function subscribeFetched(fn: FetchedListener): () => void {
  fetchedListeners.add(fn);
  return () => { fetchedListeners.delete(fn); };
}
